import { ConnectError } from '@bufbuild/connect';
import { Empty } from '@bufbuild/protobuf';
import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import api from '@/shared/api/api';
import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import { ALLOWED_URLS, PATHS, UserBadRequestError } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useMedia,
  useRetryCountdown,
} from '@/shared/hooks';
import {
  clearDemoSession,
  clearPresignMFAExempt,
  markDemoSession,
  markPresignMFAExempt,
  normalizeEmail,
  safeJSONParse,
  trackEvent,
} from '@/shared/lib';
import {
  Button,
  InlineFormError,
  Input,
  InputPassword,
  Spinner,
} from '@/shared/ui';

import { authModel } from '@/entities/auth';

import { useLogout } from '@/features/logout';
import { ForcedMFAEnrollment, MFASignInStep } from '@/features/mfa';
import {
  clearPresignUrl,
  decodePresignUrl,
  getPresignUrl,
  hasAllRequiredPresignParams,
  parsePresignParams,
  parseTimestampForProtobuf,
  urlHasPresignParams,
} from '@/features/presignAuth';

import { MobileHeader } from '@/widgets/Header';

import {
  SESSION_CHECK_TIMEOUT_MS,
  SignInFormPayload,
  signInSchema,
} from './config';
import styles from './SignIn.module.scss';

export const SignIn: FC = () => {
  const [isPreSignLoading, setIsPreSignLoading] = useState(false);
  // Kept for the analytics event fired once the second factor completes.
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isCountdownAfterFailure, setIsCountdownAfterFailure] = useState(false);

  const dispatch = useAppDispatch();

  const logout = useLogout();

  const { secondsLeft, startCountdown } = useRetryCountdown();
  const isRetryCountdownActive = secondsLeft > 0;

  const pendingMFARequirement = useAppSelector(
    authModel.selectors.selectPendingMFARequirement,
  );

  const { formatMessage } = useIntl();

  const { isMobile } = useMedia();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const redirectToParam = searchParams.get('redirectTo');

  const initialRedirectURL =
    (location?.state as { from: Location })?.from ?? redirectToParam;
  const [redirectURL, setRedirectURL] = useState<string | Location | null>(
    initialRedirectURL,
  );
  const fromSegmentron = searchParams.get('redirectFromSegmentron');

  const decodeParam = (param: string | null): string => {
    if (!param) {
      return '';
    }
    try {
      return atob(param);
    } catch {
      return param;
    }
  };

  const emailParam = decodeParam(
    searchParams.get('email') || searchParams.get('e'),
  );

  const passwordParam = decodeParam(
    searchParams.get('password') || searchParams.get('p'),
  );

  const [isSessionCheckPending, setIsSessionCheckPending] = useState(
    () =>
      // Only a full page load: the app itself routes here when the user
      // stream fails, and sending that user back would loop.
      location.key === 'default' &&
      !emailParam &&
      !passwordParam &&
      !fromSegmentron &&
      !getPresignUrl() &&
      !(
        typeof initialRedirectURL === 'string' &&
        urlHasPresignParams(initialRedirectURL)
      ),
  );

  const defaultValues = {
    email: emailParam || '',
    password: passwordParam || '',
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, errors },
    setError,
    clearErrors,
  } = useForm<SignInFormPayload>({
    resolver: yupResolver(signInSchema),
    mode: 'all',
    defaultValues,
  });

  const navigateAfterSignIn = () => {
    if (redirectURL) {
      navigate(redirectURL);
    } else if (
      fromSegmentron &&
      ALLOWED_URLS.includes(new URL(fromSegmentron).hostname)
    ) {
      window.location.href = fromSegmentron;
    } else {
      navigate(PATHS.patients);
    }
  };

  const completeSignIn = (email: string) => {
    if (emailParam && passwordParam) {
      markDemoSession();
    } else {
      clearDemoSession();
    }
    clearPresignMFAExempt();

    trackEvent({
      eventName: 'Login Success',
      properties: {
        email,
      },
    });

    navigateAfterSignIn();
  };

  // The second-factor screens stay mounted until they say they are done, so
  // the requirement is cleared here rather than when the RPC resolves.
  const finishSecondFactor = () => {
    dispatch(authModel.actions.clearPendingMFARequirement());
    completeSignIn(submittedEmail);
  };

  const onSubmit: SubmitHandler<SignInFormPayload> = async (data) => {
    const { password } = data;
    const email = normalizeEmail(data.email);

    setSubmittedEmail(email);

    try {
      const startLogInResponse = await dispatch(
        authModel.thunks.startSignIn({ Email: email, Password: password }),
      ).unwrap();

      if (startLogInResponse.Status === AuthState_AuthStatus.SUCCESSFUL) {
        completeSignIn(email);
      }
    } catch (error) {
      if (error instanceof ConnectError) {
        const parsedMessage = safeJSONParse(
          (error as ConnectError)?.rawMessage,
        );

        const userBadRequestCode =
          parsedMessage.type === 'UserBadRequestError' && parsedMessage.code;
        const { retryAfterMs } = parsedMessage;

        if (userBadRequestCode === UserBadRequestError.LOGIN_INVALID_CREDS) {
          setError('root', {
            message: formatMessage({
              id: 'signIn.incorrectPasswordOrEmail',
              defaultMessage: 'Incorrect email or password.',
            }),
          });

          if (typeof retryAfterMs === 'number' && retryAfterMs > 0) {
            setIsCountdownAfterFailure(true);
            startCountdown(retryAfterMs);
          }
        } else if (userBadRequestCode === UserBadRequestError.LOGIN_THROTTLED) {
          if (typeof retryAfterMs === 'number' && retryAfterMs > 0) {
            clearErrors('root');
            setIsCountdownAfterFailure(false);
            startCountdown(retryAfterMs);
          } else {
            setError('root', {
              message: formatMessage({
                id: 'signIn.waitBeforeRetry',
                defaultMessage:
                  'Please wait a few seconds before signing in again.',
              }),
            });
          }
        } else if (
          userBadRequestCode === UserBadRequestError.PASSWORD_RESET_REQUIRED
        ) {
          setError('root', {
            message: formatMessage({
              id: 'signIn.passwordResetRequired',
              defaultMessage:
                'Your password is too short to use without two-step verification. Reset it to sign in.',
            }),
          });
        }
      }
    }
  };

  const onClearErrors = () => {
    clearErrors();
  };

  const retryCountdownMessage = isCountdownAfterFailure
    ? formatMessage(
        {
          id: 'signIn.incorrectPasswordOrEmailRetryCountdown',
          defaultMessage:
            'Incorrect email or password. Try again in {seconds, plural, one {# second} other {# seconds}}.',
        },
        { seconds: secondsLeft },
      )
    : formatMessage(
        {
          id: 'signIn.retryCountdown',
          defaultMessage:
            'Too many attempts. Try again in {seconds, plural, one {# second} other {# seconds}}.',
        },
        { seconds: secondsLeft },
      );

  const formErrorMessage = isRetryCountdownActive
    ? retryCountdownMessage
    : errors.root?.message;

  useEffect(() => {
    const handlePreSignAuth = async () => {
      const preSignURL = getPresignUrl();

      const redirectHasPresignParams =
        typeof redirectURL === 'string' && urlHasPresignParams(redirectURL);

      const segmentronHasPresignParams = urlHasPresignParams(fromSegmentron);

      if (
        redirectHasPresignParams ||
        segmentronHasPresignParams ||
        preSignURL
      ) {
        setIsPreSignLoading(true);

        const getPresignUrlString = () => {
          if (redirectHasPresignParams) {
            return redirectURL as string;
          }

          if (segmentronHasPresignParams) {
            return fromSegmentron;
          }

          return preSignURL;
        };

        const urlString = getPresignUrlString();
        const decodedUrlString = decodePresignUrl(urlString as string);

        const urlWithPresignParams = new URL(
          decodedUrlString,
          window.location.origin,
        );

        const presignParams = parsePresignParams(
          urlWithPresignParams.searchParams,
        );
        const { required, optional } = presignParams;

        if (hasAllRequiredPresignParams(required)) {
          if (redirectHasPresignParams) {
            setRedirectURL(null);
          }

          const response = await dispatch(
            authModel.thunks.signInByPresign({
              Signature: required.signature!,
              ClientKeyID: required.clientKeyID!,
              SignatureCreationTimestampUnixMilli: parseTimestampForProtobuf(
                required.timestampUnixMilli!,
              ),
              UserID: required.userID!,
              ...(optional.organizationID && {
                OrganizationID: optional.organizationID,
              }),
              ...(optional.reportID && {
                ReportID: optional.reportID,
              }),
            }),
          ).unwrap();

          if (response.Status === AuthState_AuthStatus.SUCCESSFUL) {
            clearPresignUrl();
            markPresignMFAExempt();

            if (segmentronHasPresignParams) {
              window.location.href = urlWithPresignParams.href;
            } else {
              navigate(
                urlWithPresignParams.pathname + urlWithPresignParams.search,
              );
            }
          }
        }
      }
    };

    handlePreSignAuth().finally(() => {
      setIsPreSignLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isSessionCheckPending) {
      return;
    }

    api.auth
      .mFA_Status(new Empty(), { timeoutMs: SESSION_CHECK_TIMEOUT_MS })
      .then(() => navigateAfterSignIn())
      .catch(() => setIsSessionCheckPending(false));
  }, []);

  useEffect(() => {
    if (isSessionCheckPending) {
      return;
    }

    trackEvent({
      eventName: 'Signin Page Viewed',
      isPageViewEvent: true,
      ...(emailParam && {
        properties: {
          email: emailParam,
        },
      }),
    });
  }, [isSessionCheckPending, emailParam]);

  if (isSessionCheckPending) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner} size={96} />
      </div>
    );
  }

  if (pendingMFARequirement === 'VerifyRequired') {
    return (
      <MFASignInStep
        onVerified={finishSecondFactor}
        onBackToSignIn={() =>
          dispatch(authModel.actions.clearPendingMFARequirement())
        }
      />
    );
  }

  if (pendingMFARequirement === 'EnrollRequired') {
    return (
      <ForcedMFAEnrollment
        onCompleted={finishSecondFactor}
        onSignOut={logout}
      />
    );
  }

  if (isMobile) {
    return (
      <div>
        <MobileHeader isProtectedPages={false} />

        <div className={styles.mobileWrapper}>
          <h3 className={cn(styles.mobileTitle, 'h2m')}>
            {formatMessage({
              id: 'signIn.header',
              defaultMessage: 'Sign in',
            })}
          </h3>

          <form
            id="signinform"
            onSubmit={handleSubmit(onSubmit)}
            onChange={onClearErrors}
          >
            <Controller
              control={control}
              name="email"
              render={({
                field: { ref, value, name, onBlur, onChange },
                fieldState: { error, isTouched },
              }) => (
                <Input
                  className={styles.mobileEmail}
                  required
                  ref={ref}
                  value={value || ''}
                  name={name}
                  inputMode="email"
                  type="email"
                  onBlur={onBlur}
                  onChange={(event) => {
                    if (!isTouched) {
                      setValue(name, event.target.value);
                    } else {
                      onChange(event);
                    }
                  }}
                  label={formatMessage({
                    id: 'global.email',
                    defaultMessage: 'Email',
                  })}
                  error={error?.message}
                  autoComplete="email"
                />
              )}
            />

            <Controller
              control={control}
              name="password"
              render={({
                field: { ref, value, name, onBlur, onChange },
                fieldState: { error },
              }) => (
                <InputPassword
                  className={cn(
                    styles.password,
                    formErrorMessage && styles.mobilePasswordWithError,
                  )}
                  required
                  ref={ref}
                  value={value || ''}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  label={formatMessage({
                    id: 'signIn.password',
                    defaultMessage: 'Password',
                  })}
                  error={error?.message}
                  autoComplete="current-password"
                />
              )}
            />

            {formErrorMessage && (
              <InlineFormError
                className={styles.mobileFormError}
                errorMessage={formErrorMessage}
              />
            )}

            <div className={styles.mobileFormFooter}>
              <Button
                data-component="sign-in-submit-button"
                type="submit"
                form="signinform"
                loading={isSubmitting || isPreSignLoading}
                disabled={isRetryCountdownActive}
              >
                {formatMessage({
                  id: 'signIn.submit',
                  defaultMessage: 'Sign in',
                })}
              </Button>

              <Button
                variant="tertiary"
                disabled={isSubmitting || isPreSignLoading}
                onClick={() => navigate(PATHS.resetPassword)}
              >
                {formatMessage({
                  id: 'signIn.forgotPassword',
                  defaultMessage: 'Forgot password',
                })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} data-section="sign-in">
      <div className={styles.wrapper}>
        <h3 className={cn(styles.header, 'h3')}>
          <FormattedMessage id="signIn.header" defaultMessage="Sign in" />
        </h3>
        <form
          id="signinform"
          onSubmit={handleSubmit(onSubmit)}
          onChange={onClearErrors}
        >
          <Controller
            control={control}
            name="email"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error, isTouched },
            }) => (
              <Input
                className={styles.email}
                required
                ref={ref}
                value={value || ''}
                name={name}
                inputMode="email"
                type="email"
                onBlur={onBlur}
                onChange={(event) => {
                  if (!isTouched) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                label={formatMessage({
                  id: 'global.email',
                  defaultMessage: 'Email',
                })}
                error={error?.message}
                autoComplete="email"
              />
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <InputPassword
                className={styles.password}
                required
                ref={ref}
                value={value || ''}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                label={formatMessage({
                  id: 'signIn.password',
                  defaultMessage: 'Password',
                })}
                error={error?.message}
                autoComplete="current-password"
              />
            )}
          />

          {formErrorMessage && (
            <InlineFormError errorMessage={formErrorMessage} />
          )}

          <div className={styles.formFooter}>
            <Button
              variant="tertiary"
              disabled={isSubmitting}
              onClick={() => navigate(PATHS.resetPassword)}
              size={isMobile ? 'medium' : 'large'}
            >
              <FormattedMessage
                id="signIn.forgotPassword"
                defaultMessage="Forgot password"
              />
            </Button>
            <Button
              type="submit"
              form="signinform"
              loading={isSubmitting}
              disabled={isRetryCountdownActive}
              size={isMobile ? 'medium' : 'large'}
            >
              <FormattedMessage id="signIn.submit" defaultMessage="Sign in" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
